import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockDisputes } from '@/mocks/disputes';
import { mockPartners } from '@/mocks/partners';
import { mockLeads } from '@/mocks/leads';
import { mockPayoutLines } from '@/mocks/payouts';
import { AlertTriangle, Clock, CheckCircle, XCircle, MessageSquare } from 'lucide-react';
import { mockVerticals } from '@/mocks/verticals';

export default function DisputesPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [disputeStatuses, setDisputeStatuses] = useState<Record<string, string>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let result = [...mockDisputes];
    if (statusFilter !== 'all') {
      result = result.filter(d => d.status === statusFilter);
    }
    return result.sort((a, b) => new Date(b.raised_at).getTime() - new Date(a.raised_at).getTime());
  }, [statusFilter]);

  const statusBadge = (status: string) => {
    switch (status) {
      case 'pending': return { bg: 'var(--warning-bg)', color: 'var(--warning-text)', border: 'var(--warning-border)' };
      case 'under_review': return { bg: 'var(--bg-input)', color: 'var(--text-muted)', border: 'var(--border-primary)' };
      case 'accepted': return { bg: 'var(--success-bg)', color: 'var(--success-text)', border: 'var(--success-border)' };
      case 'rejected': return { bg: 'var(--danger-bg)', color: 'var(--danger-text)', border: 'var(--danger-border)' };
      case 'escalated': return { bg: 'var(--bg-input)', color: 'var(--text-muted)', border: 'var(--border-primary)' };
      default: return { bg: 'var(--bg-input)', color: 'var(--text-tertiary)', border: 'var(--border-primary)' };
    }
  };

  const handleAccept = (id: string) => {
    setDisputeStatuses(prev => ({ ...prev, [id]: 'accepted' }));
  };

  const handleReject = (id: string) => {
    setDisputeStatuses(prev => ({ ...prev, [id]: 'rejected' }));
  };

  const handleRequestInfo = (id: string) => {
    // Show a simple toast-like feedback — for now just update state
    setDisputeStatuses(prev => ({ ...prev, [id]: 'under_review' }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Disputes</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{filtered.length} disputes</p>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {['all', 'pending', 'under_review', 'accepted', 'rejected'].map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-all duration-200 whitespace-nowrap filter-btn${statusFilter === s ? ' is-active' : ''}`}
          >
            {s === 'all' ? 'All' : s.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      <div className="rounded-xl overflow-hidden" style={{ background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)' }}>
        <div className="divide-y" style={{ borderColor: 'var(--border-separator)' }}>
          {filtered.map(d => {
            const partner = mockPartners.find(p => {
              const pl = mockPayoutLines.find(pl => pl.id === d.payout_line_id);
              return pl?.partner_id === p.id;
            });
            const lead = mockLeads.find(l => l.id === d.lead_id);
            const payout = mockPayoutLines.find(p => p.id === d.payout_line_id);
            const vertical = mockVerticals.find(v => v.id === lead?.vertical_id);
            const daysOpen = Math.floor((Date.now() - new Date(d.raised_at).getTime()) / (1000 * 60 * 60 * 24));
            const badge = statusBadge(d.status);

            return (
              <div
                key={d.id}
                className="p-5 transition-colors cursor-pointer hover-surface"
                onClick={() => setExpandedId(expandedId === d.id ? null : d.id)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border"
                        style={{ background: badge.bg, color: badge.color, borderColor: badge.border }}
                      >
                        {(disputeStatuses[d.id] || d.status).replace(/_/g, ' ')}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{d.reason.replace(/_/g, ' ')}</span>
                      {daysOpen > 7 && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border"
                          style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', borderColor: 'var(--danger-border)' }}
                        >
                          <AlertTriangle size={10} />
                          {daysOpen}d open
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium mt-2" style={{ color: 'var(--text-secondary)' }}>
                      {partner?.display_name} — {lead?.firstname} {lead?.lastname}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>€{payout?.amount_eur} at stake</p>
                    {d.detail && (
                      <p
                        className="text-xs mt-2 rounded-lg p-2.5 border"
                        style={{ background: 'var(--bg-input)', color: 'var(--text-tertiary)', borderColor: 'var(--border-primary)' }}
                      >
                        {d.detail}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 ml-4 shrink-0" onClick={e => e.stopPropagation()}>
                    <button
                      className="p-1.5 rounded-lg transition-colors hover-success-bg"
                      style={{ color: 'var(--success-text)' }}
                      title="Accept"
                      onClick={() => handleAccept(d.id)}
                    >
                      <CheckCircle size={16} />
                    </button>
                    <button
                      className="p-1.5 rounded-lg transition-colors hover-danger-bg"
                      style={{ color: 'var(--danger-text)' }}
                      title="Reject"
                      onClick={() => handleReject(d.id)}
                    >
                      <XCircle size={16} />
                    </button>
                    <button
                      className="p-1.5 rounded-lg transition-colors hover-surface"
                      style={{ color: 'var(--text-muted)' }}
                      title="Request info"
                      onClick={() => handleRequestInfo(d.id)}
                    >
                      <MessageSquare size={16} />
                    </button>
                  </div>
                </div>

                {expandedId === d.id && (
                  <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-separator)' }}>
                    <div className="space-y-3">
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Lead: {lead?.firstname} {lead?.lastname} · {vertical?.name} · {lead?.city}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Amount at stake: €{payout?.amount_eur}</p>
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => handleAccept(d.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-all"
                          style={{ background: 'var(--success-bg)', color: 'var(--success-text)', borderColor: 'var(--success-border)' }}
                        >
                          <CheckCircle size={12} />
                          Accept Dispute
                        </button>
                        <button
                          onClick={() => handleReject(d.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border transition-all"
                          style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', borderColor: 'var(--danger-border)' }}
                        >
                          <XCircle size={12} />
                          Reject Dispute
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-center py-8">
              <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No disputes</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}